import { useMemo, useState } from "react";

import { Table } from "@similarweb/pro-logic-components";
import { EmptyState } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { AppsDigitalAssetRecord } from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";
import { getNoData } from "app/pages/sales-intelligence/pages/company-analysis/constants";
import { APPS_PAGE_SIZE } from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/DigitalAssetsCard/constants";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { DigitalAssetsLoader } from "../DigitalAssetsLoader";
import { StyledTableContainer } from "../styles";

import { getAppsColumns } from "./columns";

const EMPTY_APPS: readonly AppsDigitalAssetRecord[] = [];

type Props = {
    data?: readonly AppsDigitalAssetRecord[];
    isLoading: boolean;
};

export const AppsTabContent = ({ data = EMPTY_APPS, isLoading }: Props) => {
    const columnsConfig = getAppsColumns(data.length);

    const [page, setPage] = useState(1);

    const paginationProps = useMemo(
        () => ({
            pageSize: APPS_PAGE_SIZE,
            total: data.length,
            current: page,
            onChange(_currentPage: number, _pageSize: number, nextPage: number) {
                setPage(nextPage);
            },
        }),
        [data, page],
    );

    if (isLoading) {
        return <DigitalAssetsLoader />;
    }

    if (!salesSettingsHelper.isConsolidatedAppsEnabled) {
        return (
            <Box padding="24px">
                <EmptyState
                    title={translate("si.company-analysis.digital-assets.apps.empty.title")}
                    subtitle={translate("si.company-analysis.digital-assets.apps.empty.subtitle")}
                />
            </Box>
        );
    }

    return (
        <StyledTableContainer>
            <Table
                isLoading={isLoading}
                data={data as AppsDigitalAssetRecord[]}
                columnConfig={columnsConfig}
                pagination={paginationProps}
                noData={getNoData()}
                bordersConfig={{ body: false, header: false }}
                edgeColumnsPadding="md"
                withArrowButtons={false}
            />
        </StyledTableContainer>
    );
};
